<template>
    <div ref="map" id="map">
    </div>
</template>

<script lang="ts">

    import * as esriLoader from 'esri-loader';
    import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
    import { State, namespace } from 'vuex-class';
    import map from '@/types/map/type-map'
    import InfoWindowContent from '../InfoWindowContent.vue';
    import InfoWindowParameters from '../InfoWindowParameters.vue';
    import ArcgisSettingsWidget from './ArcgisTreeViewWidget.vue';
    import ArcgisUnregisteredDcx from './ArcgisUnregisteredDcx.vue';
    import DcxUnregisteredArcgis from './DcxUnregisteredArcgis.vue';
    import axiosManager from '@/components/plugins/axiosGlobal/axiosManager';
    import ArcgisTreeViewWidget from './ArcgisTreeViewWidget.vue';
    import arcgisLayer from '@/types/map/type-arcgis-layer';

    import locales from './locales/ArcgisMap.json';

    const CartoModule = namespace("CartoMod");

    @Component({
        name: 'arcgis-map',
        components: {
            InfoWindowContent,
            InfoWindowParameters,
            ArcgisSettingsWidget,
            ArcgisUnregisteredDcx,
            DcxUnregisteredArcgis,
            ArcgisTreeViewWidget
        },
        i18n : { messages : locales }
    })

    export default class ArcgisMap extends Vue {

        @CartoModule.State
        private map!: map;
        @CartoModule.State
        private existingLayers!: any[];
        @CartoModule.State
        private arcgisUnregistered!: object[];
        @CartoModule.State
        private dcxUnregistered!: object[];
        @CartoModule.State
        private mapIsInitialized!: boolean;

        @CartoModule.Mutation
        private setExistingLayers!: (layers: any[]) => void;
        @CartoModule.Mutation
        private setArcgisData!: (Invariants: object[]) => void;
        @CartoModule.Mutation
        private setLayersParametersJSON!: (layersJSON: string) => void;
        @CartoModule.Mutation
        private setMapBasemap!: (basemap: object | string) => void;

        //Props
        @Prop()
        private adminAccess!: boolean;
        @Prop()
        private isWidget!: boolean;
        @Prop({ default: () => ([2.3514616, 48.8566969]) })
        private center!: google.maps.LatLngLiteral;
        @Prop({ default: 6 })
        private zoom!: number;
        @Prop({ default: 0 })
        private minZoom!: number;
        @Prop({ default: false })
        private disableDefaultUI!: boolean;
        @Prop({ default: 0 })
        private layerId!: number;
        @Prop()
        private widgetNodeId!: string;

        @Watch('arcgisUnregistered')
        setArcgisUnregisteredNb() {
            this.expandArcgisUnregisteredDcx.iconNumber = this.arcgisUnregistered.length
        }

        @Watch('dcxUnregistered')
        setDcxUnregisteredNb() {
            this.expandDcxUnregisteredArcgis.iconNumber = this.dcxUnregistered.length
        }

        arcgisTreeViewWidget: ArcgisTreeViewWidget = new ArcgisTreeViewWidget({
            el: document.createElement('div'),
            store: this.$store
        })

        arcgisUnregisteredDcx: ArcgisUnregisteredDcx = new ArcgisUnregisteredDcx({
            el: document.createElement('div'),
            store: this.$store
        })
        dcxUnregisteredArcgis: DcxUnregisteredArcgis = new DcxUnregisteredArcgis({
            el: document.createElement('div'),
            store: this.$store
        })
        arcgisInfoWindowParameters: InfoWindowParameters = new InfoWindowParameters({
            el: document.createElement('div'),
            store: this.$store
        })

        private expandArcgisUnregisteredDcx: any = {};
        private expandDcxUnregisteredArcgis: any = {};

        private arcgisAllFeatures: any[] = []
        //arcgisPopupcontent: InfoWindowContent = new InfoWindowContent({
        //    el: document.createElement('div'),
        //    store: this.$store
        //})

        mounted() {
            //console.log(this.map.layersParametersJSON)
            esriLoader.setDefaultOptions({ css: true });
            esriLoader.loadModules([
                'esri/Map',
                'esri/views/MapView',
                'esri/layers/MapImageLayer',
                'esri/widgets/LayerList',
                'esri/layers/FeatureLayer',
                'esri/geometry/Point',
                'esri/geometry/Extent',
                'esri/widgets/BasemapGallery',
                'esri/PopupTemplate',
                'esri/widgets/Expand',
                'esri/core/watchUtils',
                'esri/Basemap'
            ])
                .then(([
                    Map,
                    MapView,
                    MapImageLayer,
                    LayerList,
                    FeatureLayer,
                    Point,
                    Extent,
                    BasemapGallery,
                    PopupTemplate,
                    Expand,
                    WatchUtils,
                    Basemap
                ]) => {
                    var map = new Map({
                        basemap: this.map.basemapJSON == null ? 'hybrid' : Basemap.fromJSON(JSON.parse(this.map.basemapJSON!))
                    });
                    map.watch('basemap', (newValue) => {
                        this.setMapBasemap(JSON.stringify(newValue.toJSON()));
                    })

                    if (this.map.url !== '') {
                        var layer = new MapImageLayer({
                            url: this.map.url,
                            sublayers: this.map.layersParametersJSON != null ? this.getLayers(JSON.parse(this.map.layersParametersJSON!)).reverse() : null
                        })
                        layer.when(() => {
                            this.setExistingLayers(layer.sourceJSON.layers);
                            (this.arcgisTreeViewWidget as any).showParams(this.existingLayers)
                            for (let sublayer of layer.sublayers.items) {
                                sublayer.createFeatureLayer()
                                    .then((featureLayer) => featureLayer.load())
                                    .then((featureLayer) => {
                                        var stockedTemplate = featureLayer.createPopupTemplate();
                                        sublayer.popupTemplate = new PopupTemplate({
                                            title: stockedTemplate.title,
                                            content: this.getContent
                                        })

                                        //Clustering only works on FeatureLayer and CSVLayer
                                        //featureLayer.featureReduction = {
                                        //    type: "cluster",
                                        //    clusterRadius: 100,
                                        //    popupTemplate: {
                                        //    content: "This cluster"
                                        //    }
                                        //}

                                        var loopsNb;
                                        var initQuery = featureLayer.createQuery();
                                        initQuery.where = "OBJECTID > 0";

                                        featureLayer.queryFeatureCount(initQuery).then((result) => {
                                            loopsNb = Math.ceil(result / 1000);
                                        }).then(async () => {
                                            for (let i = 0; i < loopsNb; i++) {

                                                var lastId = (this.arcgisAllFeatures.slice(-1)[0]) ? this.arcgisAllFeatures.slice(-1)[0].attributes.OBJECTID : 0;

                                                var query = featureLayer.createQuery();
                                                query.where = "OBJECTID >" + lastId;
                                                query.outFields = ["INVARIANT_LONG", "OBJECTID", "NOM_CONTRAT"];
                                                query.orderByFields = ["OBJECTID"];

                                                await featureLayer.queryFeatures(query).then((result) => {
                                                    for (let feature of result.features) {
                                                        var thisFeature = feature.toJSON();
                                                        thisFeature.layerId = feature.layer.layerId
                                                        this.arcgisAllFeatures.push(thisFeature)
                                                    }
                                                })
                                            }
                                        })
                                    })
                            }
                        })
                        this.setArcgisData(this.arcgisAllFeatures)
                        map.add(layer);
                    }
                    const view = new MapView({
                        center: this.center,
                        map: map,
                        container: 'map',
                        zoom: this.zoom
                    })
                    if (this.isWidget == true) {
                        //WIP
                        //var initQuery = featureLayer.createQuery();
                        //initQuery.where = "OBJECTID > 0";
                    }
                    const layerList = new LayerList({
                        view: view
                    })

                    this.expandArcgisUnregisteredDcx = new Expand({
                        expandIconClass: "esri-icon-notice-round",
                        view: view,
                        content: this.arcgisUnregisteredDcx.$el,
                        iconNumber: this.arcgisUnregistered.length
                    })

                    this.expandDcxUnregisteredArcgis = new Expand({
                        expandIconClass: "esri-icon-notice-round",
                        view: view,
                        content: this.dcxUnregisteredArcgis.$el,
                        iconNumber: this.dcxUnregistered.length
                    })

                    const basemapGallery = new BasemapGallery({
                        view: view,
                        container: document.createElement('div')
                    })
                    const expandBaseMap = new Expand({
                        view: view,
                        content: basemapGallery
                    })
                    const expandArcgisInfoWindowParameters = new Expand({
                        expandIconClass: "esri-icon-configure-popup",
                        view: view,
                        content: this.arcgisInfoWindowParameters.$el,
                    })

                    view.when(() => {
                        if (this.isWidget != true) {
                            if (this.arcgisUnregistered.length > 0) {
                                view.ui.add(this.expandArcgisUnregisteredDcx, 'bottom-left')
                            }
                            if (this.dcxUnregistered.length > 0) {
                                view.ui.add(this.expandDcxUnregisteredArcgis, 'bottom-right')
                            }
                            if (this.adminAccess == true) {
                                view.ui.add(this.arcgisTreeViewWidget.$el, 'top-right')
                                view.ui.add(expandBaseMap!, 'top-right')
                                view.ui.add(expandArcgisInfoWindowParameters!, 'top-right')
                            }
                            else {
                                view.ui.add(layerList, 'top-right');
                            }
                        }
                    })
                })
                .catch(err => {
                    console.error(err);
                });
        }
        //mounted end

        getContent(feature) {
            if (feature.graphic.sourceLayer.selectorDcx == null) {
                return this.$t('ErrorDCxSelector').toString() // "Erreur : Le selecteur DCX n'est pas défini sur ce type de marqueur"
            }
            else {
                var div = document.createElement('div')
                return this.$Direct.postAwaitable("carto/getNodePropsByProp", { NodeInvar: feature.graphic.attributes[feature.graphic.sourceLayer.selectorDcx] }).then((result) => {
                    const popupContent = new InfoWindowContent({
                        el: document.createElement('div'),
                        propsData: {
                            infoNodeId: result.NodeId,
                            infoType: result.NodeType,
                            infoInvariant: result.NodeInvar
                        },
                        store : this.$store
                    })
                    return popupContent.$el;
                })
                    .catch((error) => {
                        div.innerHTML = error.response.statusText
                        return div;
                    })
            }
        }

        getLayers(setupLayers: arcgisLayer[]) {
            //console.log(setupLayers)
            var selectedLayers: arcgisLayer[] = setupLayers.filter(x => x.selected == true)
            //console.log(selectedLayers)
            for (let selectedLayer of selectedLayers) {
                if (selectedLayer.isFolder == true) {
                    selectedLayer.sublayers = this.getLayers(selectedLayer.sublayers)
                }
            }
            return selectedLayers;
        }

        getAllLayers(allLayers: any[], selectedLayers: string, parentExist: boolean) {
            //console.log("passed by getAllLayers")
            var children: any[] = [];
            for (let layer of allLayers) {
                var newLayer: any;
                var exists: boolean = parentExist || JSON.parse(selectedLayers).some(x => x.p.id === layer.p.id);
                var sublayers: any[] = (layer.children) ? this.getAllLayers(layer.children, selectedLayers, exists) : [];
                if (sublayers.length > 0)
                    newLayer = { id: layer.p.id, title: layer.p.name, sublayers: sublayers, visible: layer.p.defaultVisibility };
                else
                    newLayer = { id: layer.p.id, title: layer.p.name, visible: layer.p.defaultVisibility };
                if (exists || sublayers.length > 0)
                    children.push(newLayer);
            }
            if (children.length > 0 || this.mapIsInitialized == true) {
                this.setLayersParametersJSON(JSON.stringify(children));
            }

            this.$store.dispatch("CartoMod/SetMapIsInitialized").then(response => {
					console.log("ArcgisMap - Initialisation de la map terminée");
				});
            return children;
        }
    }
</script>

<style>
    .esri-popup__content {
        overflow: hidden !important
    }
</style>

<style scoped>

    #map {
        height: 100%;
    }

    .mapWrapper {
        height: 100%;
    }
</style>